import React from "react";
import Link from "next/link";
import { SectionHeader } from "../components/section-header";
import { SectionLoader } from "../components/section-loader";
import { EmptyState } from "@/components/shared/empty-state";
import { EditorialCard } from "../components/editorial-card";
import { Newspaper, TrendingUp, Clock } from "lucide-react";
import type { HomepageNews } from "@/components/homepage/types";

interface BreakingNewsSectionProps {
  items: HomepageNews[];
  isLoading: boolean;
}

export function BreakingNewsSection({ items, isLoading }: BreakingNewsSectionProps) {
  const displayItems = items.length > 0 ? items : [];

  if (displayItems.length === 0 && !isLoading) return null;

  return (
    <section className="border-b border-border bg-neutral-50 overflow-hidden" aria-label="Breaking news">
      <div className="flex items-center h-9">
        <div className="shrink-0 bg-primary text-primary-foreground px-3 py-1 text-[0.625rem] font-bold tracking-widest uppercase">
          Breaking
        </div>
        <div className="flex-1 overflow-hidden" aria-live="off">
          <div className="flex items-center gap-8 whitespace-nowrap px-4">
            {displayItems.map((item, i) => (
              <React.Fragment key={item.id ?? i}>
                <Link href={`/news/${item.id}`} className="text-xs text-muted-foreground hover:text-foreground transition-colors truncate max-w-md">
                  {item.title}
                </Link>
                <span className="text-neutral-300" aria-hidden="true">·</span>
              </React.Fragment>
            ))}
            {displayItems.map((item, i) => (
              <React.Fragment key={`dup-${item.id ?? i}`}>
                <Link href={`/news/${item.id}`} className="text-xs text-muted-foreground hover:text-foreground transition-colors truncate max-w-md">
                  {item.title}
                </Link>
                <span className="text-neutral-300" aria-hidden="true">·</span>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .border-b\\[border-border\\]\\[bg-neutral-50\\] overflow-hidden > div > div > div {
            animation: none;
          }
        }
      `}</style>
    </section>
  );
}